import React, { useState, useEffect } from "react";
import { Menu, X, Phone, Mail, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.filter(item => item.label !== "Pure Detail Booking").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-primary-foreground font-medium text-base transition-all hover:text-primary-foreground/80 hover:underline hover:decoration-primary-foreground hover:decoration-2 hover:underline-offset-4">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-6">
      {menuItems.filter(item => item.label !== "Pure Detail Booking").map((item, i) => <Link key={item.id} to={item.href || "#"} className="block py-3 text-lg font-medium text-foreground border-b border-border/20 hover:text-primary transition-colors" onClick={() => setTimeout(() => onClose(), 100)} data-index={i}>
          {item.label}
        </Link>)}
      <div className="mt-6 flex flex-col gap-4">
        <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-none h-12 text-lg font-bold shadow-none border-none transition-transform active:scale-95">
          <Link to="/PureDetailBookingPage">Book Now</Link>
        </Button>
        <Button asChild className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-none h-12 text-lg font-bold shadow-none border-none transition-transform active:scale-95">
          <Link to="tel:5126359324">
            <Phone className="mr-2 h-5 w-5" /> (512) 635-9324
          </Link>
        </Button>
        <Button asChild className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-none h-12 text-base font-bold shadow-none border-none transition-transform active:scale-95">
          <Link to="mailto:puredetailcleaningatx@gmail.com">
            <Mail className="mr-2 h-5 w-5" /> puredetailcleaningatx@gmail.com
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function PureDetailHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="pure-detail-header" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-in-out ${isScrolled ? "bg-primary/95 backdrop-blur-[12px] shadow-md" : "bg-gradient-to-r from-primary to-primary/90 border-b border-primary-foreground/10"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0 group transition-transform hover:scale-105">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562197/img/image_7zs82o2e.png" alt="Pure Detail Cleaning" className="h-24 w-auto object-contain brightness-0 invert" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="44" className="flex-1">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Button asChild className="bg-transparent text-primary-foreground border-2 border-primary-foreground/40 hover:bg-primary-foreground/10 font-bold rounded-none px-4 h-10 transition-all hover:scale-105 shadow-sm">
            
          </Button>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 border-2 border-primary-foreground/20 font-extrabold rounded-none px-6 h-10 transition-all hover:scale-105 shadow-md ml-2">
            
          </Button>
        </div>

        <Button variant="ghost" size="icon" className="md:hidden text-primary-foreground hover:text-primary-foreground/80" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background md:hidden animate-in slide-in-from-right duration-300 pointer-events-none">
          <div className="flex items-center justify-between p-4 border-b border-border/10 h-20 pointer-events-auto">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562197/img/image_7zs82o2e.png" alt="Pure Detail Cleaning" className="h-24 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="overflow-y-auto h-[calc(100vh-80px)] pointer-events-auto">
            <MenuProvider menu_id="44">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </header>;
}